import Vue from 'vue'

/**
 * Pin do mapa montado por cor + ícone Font Awesome. O Google Maps só aceita imagem como marcador, então o
 * pin é desenhado num canvas (gota colorida com o ícone na cor escolhida, branca por padrão) e vira data URL, guardada em cache
 * por cor + ícone. O glifo vem da própria fonte do Font Awesome já carregada na página.
 *
 * `pinMarkerUrl` é síncrona (devolve null até o pin ficar pronto) e reativa: quando um pin termina de ser
 * desenhado, quem a chamou numa render ou computed é recalculado.
 */
const SIZE = 96 // 2x do tamanho exibido (48), para ficar nítido em tela de alta densidade
const CX = SIZE / 2
const CY = 36
const RADIUS = 32
const TIP_Y = 94
const DOT_RADIUS = 11 // pin sem ícone: só um ponto no centro da gota
export const DEFAULT_ICON_COLOR = '#ffffff'

/** Altura do pin no mapa, em px (livre entre MIN e MAX). Sem valor, cada tela usa o seu tamanho padrão. */
export const PIN_SIZE_MIN = 24
export const PIN_SIZE_MAX = 128
export const PIN_SIZE_DEFAULT = 48

const state = Vue.observable({ version: 0 })
const cache = new Map() // chave => data URL | 'pending' | 'error'

const key = (color, iconClass, iconColor) => `${color}|${iconClass || ''}|${iconColor || ''}`

/** Escurece um #rrggbb misturando com preto (contorno do pin). */
export function darken(hex, amount = 0.3) {
  const ch = from => Math.round(parseInt(hex.slice(from, from + 2), 16) * (1 - amount))

  return `rgb(${ch(1)}, ${ch(3)}, ${ch(5)})`
}

/** Glifo, família e peso da classe do Font Awesome, lidos do CSS carregado (sem depender de tabela de códigos). */
function glyphOf(iconClass) {
  const el = document.createElement('i')
  el.className = iconClass
  el.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none'
  document.body.appendChild(el)

  try {
    const content = getComputedStyle(el, '::before').content.replace(/^["']|["']$/g, '')
    const css = getComputedStyle(el)

    return content && content !== 'none' ? { char: content, family: css.fontFamily, weight: css.fontWeight } : null
  } finally {
    document.body.removeChild(el)
  }
}

function dropPath(ctx) {
  // Pontos de tangência entre a ponta e o círculo da cabeça.
  const phi = Math.acos(RADIUS / (TIP_Y - CY))
  const start = Math.PI / 2 - phi
  const end = Math.PI / 2 + phi

  ctx.beginPath()
  ctx.arc(CX, CY, RADIUS, start, end, true)
  ctx.lineTo(CX, TIP_Y)
  ctx.closePath()
}

/** Desenha o pin e devolve a data URL (PNG). Espera a fonte do ícone carregar. */
export async function renderPinDataUrl(color, iconClass, iconColor) {
  const glyph = iconClass ? glyphOf(iconClass) : null
  if (glyph) await document.fonts.load(`${glyph.weight} 40px ${glyph.family}`, glyph.char)

  const canvas = document.createElement('canvas')
  canvas.width = SIZE
  canvas.height = SIZE
  const ctx = canvas.getContext('2d')
  const ink = iconColor || DEFAULT_ICON_COLOR

  dropPath(ctx)
  ctx.fillStyle = color
  ctx.fill()
  ctx.lineWidth = 3
  ctx.strokeStyle = darken(color)
  ctx.stroke()

  if (glyph) {
    ctx.font = `${glyph.weight} 32px ${glyph.family}`
    ctx.fillStyle = ink
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(glyph.char, CX, CY + 1)
  } else {
    ctx.beginPath()
    ctx.arc(CX, CY, DOT_RADIUS, 0, Math.PI * 2)
    ctx.fillStyle = ink
    ctx.fill()
  }

  return canvas.toDataURL('image/png')
}

/** @param {{color: string, icon_class?: string|null, icon_color?: string|null}|null} style */
export function pinMarkerUrl(style) {
  // Ler `version` faz a render/computed de quem chama depender do cache.
  // eslint-disable-next-line no-unused-expressions
  state.version
  if (!style || !style.color) return null

  const k = key(style.color, style.icon_class, style.icon_color)
  const hit = cache.get(k)
  if (hit && hit !== 'pending' && hit !== 'error') return hit
  if (hit) return null

  cache.set(k, 'pending')
  renderPinDataUrl(style.color, style.icon_class, style.icon_color)
    .then(url => cache.set(k, url))
    .catch(() => cache.set(k, 'error'))
    .then(() => { state.version += 1 })

  return null
}

/**
 * Pin de uma ocorrência: o do tipo (cor + ícone ou imagem), senão o da disciplina.
 * `usable` valida a URL de imagem (descarta a que não carrega). `size` (px) só existe no pin de cor + ícone;
 * null deixa a tela usar o tamanho padrão dela.
 * @returns {{url: string, size: number|null}|null}
 */
export function occurrencePin(occ, usable) {
  if (!occ) return null

  const fromStyle = style => {
    const url = pinMarkerUrl(style)

    return url ? { url, size: (style && style.size) || null } : null
  }
  const fromImage = url => (url ? { url, size: null } : null)

  return fromStyle(occ.dispatch_type_pin_style)
    || fromImage(usable(occ.dispatch_type_pin_url))
    || fromStyle(occ.discipline_pin_style)
    || fromImage(usable(occ.discipline_pin_url))
    || null
}

// ---- Catálogo de pins por id (tipos de ocorrência e disciplinas) ----------------------------------------------
// Mapas que desenham a ocorrência sem receber o pin no payload (detalhe, despacho, modais) resolvem por id.

const CATALOG_TTL_MS = 5 * 60 * 1000
const catalog = Vue.observable({ dispatchTypes: {}, disciplines: {} })
let catalogLoadedAt = 0
let catalogLoading = null

/** Carrega (ou renova, passado o TTL) o catálogo de pins. Falha em silêncio: o mapa segue com o pin padrão. */
export function loadPinCatalog(http, api) {
  if (catalogLoading) return catalogLoading
  if (Date.now() - catalogLoadedAt < CATALOG_TTL_MS) return Promise.resolve()

  catalogLoading = http.get(`${api}occurrence_pins`)
    .then(res => {
      const data = (res.data && res.data.data) || {}
      catalog.dispatchTypes = data.dispatch_types || {}
      catalog.disciplines = data.disciplines || {}
      catalogLoadedAt = Date.now()
    })
    .catch(() => {})
    .then(() => { catalogLoading = null })

  return catalogLoading
}

/**
 * Ícone de marcador do Google Maps para a ocorrência (pin do tipo, senão o da disciplina), ou null quando
 * nenhum dos dois tem pin: quem chama deixa o marcador padrão.
 * @param {{dispatch_type_id?: number|string, discipline_id?: number|string}} occ
 */
export function occurrenceMarkerIcon(occ, size = 44) {
  if (!occ) return null

  const type = catalog.dispatchTypes[occ.dispatch_type_id]
  const discipline = catalog.disciplines[occ.discipline_id]
  const pin = occurrencePin(
    {
      dispatch_type_pin_style: type && type.style,
      dispatch_type_pin_url: type && type.url,
      discipline_pin_style: discipline && discipline.style,
      discipline_pin_url: discipline && discipline.url,
    },
    url => url,
  )
  if (!pin) return null

  const px = pin.size || size

  return { url: pin.url, scaledSize: { width: px, height: px }, anchor: { x: px / 2, y: px } }
}
